import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import type { FinanceAccount, FinanceImport } from '@etiquetachef/types';
import { storage } from '@/lib/firebase';
import { subscribeFinanceAccounts, subscribeFinanceImports } from '@/lib/firestoreFinance';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { toast } from '@/lib/notifications';

/** Dois dígitos com zero à esquerda. */
function pad(n: number): string {
  return String(n).padStart(2, '0');
}
/** `DD/MM/AAAA HH:mm` no fuso do navegador. */
function formatDateMs(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Tela `/finance/imports`: histórico das sessões de import de extrato por
 * OCR, com a imagem original e o atalho para retomar um rascunho.
 *
 * Origem: #75 — 2026-06-21 22:25 +0200 (import de extrato bancário por
 * OCR/LLM com preview + dedupe).
 *
 * Contrato: `subscribeFinanceImports` (listener, `createdAt desc`) e
 * `subscribeFinanceAccounts` (nome da conta). "Ver imagem" pede
 * `getDownloadURL` no Storage (falha → aviso). "Importar extrato" vai para
 * `/finance/imports/new`; linha `DRAFT` tem "Reabrir" →
 * `/finance/imports/:importId`. Só leitura — nenhuma callable aqui.
 *
 * Usado por: `routes/router.tsx` (rota `finance/imports`, dentro do
 * `RequireSuperAdmin`), menu Financeiro → "Importações" (`AdminLayout`);
 * `FinanceImportFlowPage` volta para cá ao confirmar. Só `apps/admin`
 * (web). Spec: `docs/screens/finance-imports-admin.md`.
 *
 * Arquitetura: regra Firestore `match /finance_imports/{importId}` (Lê:
 * super-admin; Escreve: só servidor) e regra Storage
 * `match /finance_imports/{importId}/{fileName}` (Lê: super-admin).
 *
 * Dados: `finance_imports/{id}` (`FinanceImportSchema`) — exemplo em
 * `subscribeFinanceImports`; contagens `parsedCount`/`newCount`/`dupCount`
 * e `confirmedCount` (só exibido quando `CONFIRMED`). Datas em ms epoch,
 * exibidas no fuso do navegador.
 *
 * Issues: #75 (origem).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function FinanceImportsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [imports, setImports] = useState<FinanceImport[]>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  /** Resolve a URL de download da imagem no Storage e abre o modal. */
  async function openImage(imagePath: string): Promise<void> {
    try {
      setImageError(false);
      const url = await getDownloadURL(storageRef(storage, imagePath));
      setImageUrl(url);
      setImageOpen(true);
    } catch (err) {
      console.error('getDownloadURL failed', err);
      // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
      reportNonFatal('admin_finance_imports_page_failed', {
        site: 'getDownloadURL failed',
        code: (err as { code?: string }).code ?? null,
        ...serializeError(err),
      });
      setImageError(true);
      // Auditoria 2026-10-25 14:29 +0100 (#1021): o Alert fica abaixo da tabela, longe da linha clicada;
      // o toast (Flashbar fixo no topo) é o aviso.
      toast.error(t('admin.finance.imports.errors.imageLoadFailed'));
    }
  }

  useEffect(() => {
    const unsubA = subscribeFinanceAccounts((r) => {
      if (r.kind === 'READY') setAccounts(r.accounts);
    });
    const unsubI = subscribeFinanceImports((r) => {
      if (r.kind === 'READY') {
        setImports(r.imports);
        setLoading(false);
      } else if (r.kind === 'ERROR') {
        setError(true);
        setLoading(false);
      }
    });
    return (): void => {
      unsubA();
      unsubI();
    };
  }, []);

  const accountNameById = useMemo(
    () => new Map(accounts.map((a) => [a.id, a.name])),
    [accounts],
  );

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('admin.finance.imports.subtitle')}
          actions={
            <Button
              variant="primary"
              iconName="upload"
              onClick={(): void => {
                void navigate('/finance/imports/new');
              }}
            >
              {t('admin.finance.imports.create')}
            </Button>
          }
        >
          {t('admin.finance.imports.title')}
        </Header>
      }
    >
      {error && <Alert type="error">{t('admin.finance.imports.errorLoad')}</Alert>}
      <Table<FinanceImport>
        items={imports}
        loading={loading}
        trackBy="id"
        variant="container"
        resizableColumns
        stickyHeader
        empty={
          <Box textAlign="center" color="text-status-inactive" padding="m">
            {t('admin.finance.imports.empty')}
          </Box>
        }
        columnDefinitions={[
          {
            id: 'date',
            header: t('admin.finance.imports.columns.date'),
            cell: (i): React.ReactNode => formatDateMs(i.createdAt),
          },
          {
            id: 'account',
            header: t('admin.finance.imports.columns.account'),
            cell: (i): React.ReactNode => accountNameById.get(i.accountId) ?? i.accountId,
          },
          {
            id: 'parsed',
            header: t('admin.finance.imports.columns.parsed'),
            cell: (i): React.ReactNode => i.parsedCount,
          },
          {
            id: 'new',
            header: t('admin.finance.imports.columns.new'),
            cell: (i): React.ReactNode => (
              <Box color="text-status-success">{i.newCount}</Box>
            ),
          },
          {
            id: 'dup',
            header: t('admin.finance.imports.columns.dup'),
            cell: (i): React.ReactNode => (
              <Box color="text-status-inactive">{i.dupCount}</Box>
            ),
          },
          {
            id: 'confirmed',
            header: t('admin.finance.imports.columns.confirmed'),
            cell: (i): React.ReactNode => (i.status === 'CONFIRMED' ? i.confirmedCount : '—'),
          },
          {
            id: 'status',
            header: t('admin.finance.imports.columns.status'),
            cell: (i): React.ReactNode =>
              i.status === 'CONFIRMED' ? (
                <Badge color="green">{t('admin.finance.imports.status.CONFIRMED')}</Badge>
              ) : (
                <Badge color="blue">{t('admin.finance.imports.status.DRAFT')}</Badge>
              ),
          },
          {
            id: 'actions',
            header: t('admin.finance.imports.columns.actions'),
            cell: (i): React.ReactNode => (
              <SpaceBetween size="xs" direction="horizontal">
                <Button
                  variant="inline-link"
                  onClick={(): void => {
                    void openImage(i.imagePath);
                  }}
                >
                  {t('admin.finance.imports.viewImage')}
                </Button>
                {i.status === 'DRAFT' && (
                  <Button
                    variant="inline-link"
                    onClick={(): void => {
                      void navigate(`/finance/imports/${i.id}`);
                    }}
                  >
                    {t('admin.finance.imports.reopen')}
                  </Button>
                )}
              </SpaceBetween>
            ),
          },
        ]}
      />
      {imageError && (
        <Alert type="error">{t('admin.finance.imports.errors.imageLoadFailed')}</Alert>
      )}
      <Modal
        visible={imageOpen}
        onDismiss={(): void => {
          setImageOpen(false);
        }}
        header={t('admin.finance.imports.image')}
        size="medium"
      >
        {imageUrl !== null && (
          <img
            src={imageUrl}
            alt={t('admin.finance.imports.image')}
            style={{
              display: 'block',
              margin: '0 auto',
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
            }}
          />
        )}
      </Modal>
    </ContentLayout>
  );
}
